// src/.../Validator.tsx
import React, { useEffect, useState, useRef } from 'react';
import { InvoiceData, InvoiceRecord } from '../../types';
import { Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import './Validator.scss';

import ResultSummary from './components/ResultSummary/ResultSummary';
import {
  prepareInvoiceEntitiesByInvoiceUuid,
  executePreparedInvoice
} from '../../../../../../../../crud/management_api/invoices';
import { getAllBrandsByLocalId } from '../../../../../../../../crud/menu_api/brands';

import ProviderValidator, { ProviderPrep } from './components/ProviderValidation/ProviderValidation';
import ProductValidator, {
  ProductsPrep,
  FormItem
} from './components/ProductValidation/ProductValidation';
import BalanceValidation, { editableItemFields, globalBalanceKeys } from './components/BalanceValidation/BalanceValidation';
import {
  FaTableList,
  FaTruck,
  FaBoxesPacking, 
  FaChevronLeft, 
  FaChevronRight, 
  FaClipboardCheck,
  FaPlus,
  FaMinus,
  FaFile } from 'react-icons/fa6';


interface PreparedEntities {
  original_invoice: any;
  provider:         ProviderPrep;
  products:         ProductsPrep;
}

interface ValidatorProps {
  invoice: InvoiceRecord;
  show:    boolean;
  onClose: () => void;
  onValidated?: (invoiceId: number) => void; // Nuevo callback opcional
  fetchPurchases: () => void;
}

const Validator: React.FC<ValidatorProps> = ({ invoice, show, onClose, onValidated, fetchPurchases }) => {
  const [prepared,      setPrepared]     = useState<PreparedEntities | null>(null);
  const [loading,       setLoading]      = useState(false);
  const [validating,    setValidating]   = useState(false);
  const [step,          setStep]         = useState(1);
  const [execResult,    setExecResult]   = useState<any>(null);

  // Zoom functionality state
  const [zoomLevel, setZoomLevel] = useState(0.4);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const previewRef = useRef<HTMLDivElement>(null);
  const prevStepRef = useRef<number>(1);

  const [providerForm, setProviderForm] = useState<{ rut:string; name:string; phone_number?:string; email?:string }>({
    rut:'', name:'', phone_number:'', email:''
  });

  const [productForm,  setProductForm]  = useState<FormItem[]>([]);
  const [allBrands,    setAllBrands]    = useState<{brand_id:number;brand_name:string}[]>([]);
  const [productsState, setProductsState] = useState<ProductsPrep>({ found:[], not_found:[] });

  const [globalValues, setGlobalValues] = useState<Record<string, string>>({});
  const [itemValues,   setItemValues]   = useState<Record<string, string>>({});

  // Zoom control functions
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2.0));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.2));
  };

  const handleZoomReset = () => {
    setZoomLevel(0.4);
    setPosition({ x: 0, y: 0 });
  };

  // Pan functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 0.4) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 0.4) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (!show) {
        // Reset all state when the modal is hidden to ensure it's fresh for the next opening
        setStep(1);
        setPrepared(null);
        setExecResult(null);
        return;
    };

    setLoading(true);
    prepareInvoiceEntitiesByInvoiceUuid(invoice.invoice_uuid)
      .then(r => {
        setPrepared(r.data);
        setProductsState(r.data.products);
      })
      .catch(() => toast.error("Error cargando datos de validación"))
      .finally(() => setLoading(false));
  }, [show, invoice.invoice_uuid]);

  useEffect(() => {
    if (!prepared) return;
    getAllBrandsByLocalId(invoice.local_id).then((r: any) => setAllBrands(r.data));
  }, [prepared, invoice.local_id]);

  useEffect(() => {
    if (!prepared) return;
  
    if (prepared.provider.not_found) {
      setProviderForm({ ...prepared.provider.not_found, phone_number:'', email:'' });
    }

    const raw = prepared.original_invoice.detalleItems;
    const makeFormItem = (idx:number):FormItem => ({
      item_index:      idx,
      code:            String(raw[idx].codigo?.[0]          || ''),
      facturaName:     String(raw[idx].nombre?.[0]          || ''),
      cantidadInteres: String(raw[idx].cantidadInteres?.[0] || ''),
      unidadInteres:   String(raw[idx].unidadInteres?.[0]   || ''),
      facturaBrand:    String(raw[idx].marca?.[0]           || ''),
      accepted:        true,
      systemId:        undefined,
      systemName:      undefined,
      systemBrand:     undefined,
    });
    const allIndices = Array.from(new Set([
      ...prepared.products.found.map(f => f.item_index),
      ...prepared.products.not_found.map(n => n.item_index)
    ]));
    setProductForm(allIndices.map(makeFormItem));

    const initialGlobals = Object.fromEntries(
        globalBalanceKeys.map((key:any) => [key, String(prepared.original_invoice[key]?.[0] || '')])
    );
    setGlobalValues(initialGlobals);
  
    const initialItems: Record<string, string> = {};
    prepared.original_invoice.detalleItems.forEach((item:any, idx:number) => {
        editableItemFields.forEach((field:any) => {
            const key = `${idx}-${field}`;
            const val = Number(item[field]?.[0]);
            initialItems[key] = isNaN(val) ? '' : String(val);
        });
    });
    setItemValues(initialItems);

  }, [prepared]);

  // Re-add: handlers for product stub accept/reject
  const handleStubReject = (idx:number) => {
    if (!prepared) return;
    const item = prepared.original_invoice.detalleItems[idx];
    setProductsState(ps => {
      const exists = ps.not_found.some(n => n.item_index === idx);
      return {
        found: ps.found.filter(f => f.item_index !== idx),
        not_found: exists
          ? ps.not_found
          : [
              ...ps.not_found,
              { item_index: idx, code: item.codigo?.[0], name: item.nombre?.[0], descripcion: item.descripcion?.[0] }
            ]
      };
    });
    // Do not touch productForm here to avoid overwriting child's updateMany
  };
  const handleStubAccept = (idx:number, ingredient_id:number, ingredient_name:string) => {
    if (!prepared) return;
    const item = prepared.original_invoice.detalleItems[idx];
    setProductsState(ps => ({
      found: [
        ...ps.found,
        { item_index: idx, product_id: ingredient_id, code: item.codigo?.[0], name: item.nombre?.[0], descripcion: item.descripcion?.[0] }
      ],
      not_found: ps.not_found.filter(n => n.item_index !== idx)
    }));
    setProductForm(pf => pf.map(it =>
      it.item_index === idx
        ? { ...it, systemId:ingredient_id, systemName:ingredient_name }
        : it
    ));
  };

  // Compute accepted indices for balances
  const acceptedItemIndices = productForm.filter(it => it.accepted).map(it => it.item_index);

  // When entering step 3 (Balances), recompute global totals from accepted items only
  useEffect(() => {
    if (step === 3 && prevStepRef.current !== 3 && prepared) {
      const sumField = (field: string) =>
        acceptedItemIndices.reduce((acc, idx) => acc + Number(itemValues[`${idx}-${field}`] || 0), 0);

      setGlobalValues((gv) => ({
        ...gv,
        totalBruto: String(sumField('itemBrutoTotal')),
        totalNeto: String(sumField('itemNetoTotal')),
        totalIva: String(sumField('itemIva')),
        totalExtra: String(sumField('itemExtra')),
        totalTransporte: String(sumField('itemTransporte')),
        // Leave totalDescuento and dates as they were unless the user edits them
      }));
    }
    prevStepRef.current = step;
  }, [step, prepared, acceptedItemIndices, itemValues]);

  const buildFinal = () => {
    if (!prepared) return null;

    const finalInvoiceData = JSON.parse(JSON.stringify(prepared.original_invoice));

    for (const key in globalValues) {
      if (finalInvoiceData.hasOwnProperty(key)) {
        finalInvoiceData[key] = [globalValues[key], finalInvoiceData[key]?.[1] || 100];
      }
    }
  
    finalInvoiceData.detalleItems = finalInvoiceData.detalleItems.map((item:any, index:number) => {
      const formItem = productForm.find(pf => pf.item_index === index);
      const newItem = { ...item };

      if (formItem) {
        newItem.codigo          = [formItem.code, newItem.codigo?.[1] || 100];
        newItem.cantidadInteres = [formItem.cantidadInteres, newItem.cantidadInteres?.[1] || 100];
        newItem.unidadInteres   = [formItem.unidadInteres, newItem.unidadInteres?.[1] || 100];
        newItem.nombre          = [formItem.systemName ?? formItem.facturaName, newItem.nombre?.[1] || 100];
        newItem.marca           = [formItem.systemBrand ?? formItem.facturaBrand, newItem.marca?.[1] || 100];
      }

      // Only apply per-item balances for accepted items
      if (formItem?.accepted) {
        editableItemFields.forEach((field:any) => {
          const cellKey = `${index}-${field}`;
          if (itemValues[cellKey] !== undefined) {
            newItem[field] = [itemValues[cellKey], newItem[field]?.[1] || 100];
          }
        });
      }
    
      return newItem;
    });

    const found = productForm
      .filter(it => it.accepted && productsState.found.some(f => f.item_index === it.item_index))
      .map(it => {
        const stub = productsState.found.find(f => f.item_index === it.item_index)!;
        return {
          item_index:      it.item_index,
          product_id:      stub.product_id,
          code:            it.code,
          name:            it.systemName ?? it.facturaName,
          cantidadInteres: it.cantidadInteres,
          unidadInteres:   it.unidadInteres,
          marca:           it.systemBrand ?? it.facturaBrand
        };
      });
    const not_found = productForm
      .filter(it => it.accepted && productsState.not_found.some(n => n.item_index === it.item_index))
      .map(it => ({
        item_index:      it.item_index,
        code:            it.code,
        name:            it.systemName ?? it.facturaName, // include user-entered custom name
        cantidadInteres: it.cantidadInteres,
        unidadInteres:   it.unidadInteres,
        marca:           it.systemBrand ?? it.facturaBrand
      }));
    
    const foundBrandIds   = new Set<number>();
    const notFoundBrandNs = new Set<string>();
    productForm.forEach(it => {
        if (!it.accepted) return;
        if (it.systemBrand) {
            const b = allBrands.find(b => b.brand_name === it.systemBrand);
            b ? foundBrandIds.add(b.brand_id) : notFoundBrandNs.add(it.systemBrand);
        } else if (it.facturaBrand) {
            notFoundBrandNs.add(it.facturaBrand);
        }
    });

    return {
      invoice_id:       invoice.invoice_id,
      original_invoice: finalInvoiceData,
      provider: {
        ...prepared.provider,
        not_found: prepared.provider.not_found
          ? { ...prepared.provider.not_found, ...providerForm }
          : null
      },
      products: { found, not_found },
      brands: {
        found:     Array.from(foundBrandIds),
        not_found: Array.from(notFoundBrandNs)
      }
    };
  };

  const handleValidate = async () => {
    const user    = JSON.parse(localStorage.getItem('user')||'{}');
    const localId = user?.local_id || invoice.local_id;
    const payload = buildFinal();
  
    if (!payload) return;

    setValidating(true);
    try {
      (payload as any).local_id = localId;
      const resp = await executePreparedInvoice(payload);
      setExecResult(resp.data);
      toast.success("Ejecución exitosa");
      setStep(4); // Move to the results step
      if (onValidated) onValidated(invoice.invoice_id); // Notificar al padre
    } catch {
      toast.error("Error al ejecutar");
    } finally {
        setValidating(false);
    }
  };

  const steps = [
    { id: 1, name: "Proveedor", icon: <FaTruck/> },
    { id: 2, name: "Productos", icon: <FaBoxesPacking/> },
    { id: 3, name: "Balances", icon: <FaTableList/> },
    { id: 4, name: "Resumen", icon: <FaClipboardCheck/> },
  ];

  return (
    <Modal dialogClassName="validator-layout" show={show} onHide={onClose} size="xl" centered>
        <Modal.Body>
            <div className="validator-main-content">
                <div className="invoice-preview-container">
                    <div className="preview-header">
                        <h4>Vista Previa de Factura</h4>
                        <div className="zoom-controls">
                            <button className="zoom-btn" onClick={handleZoomOut} title="Alejar">
                                <FaMinus />
                            </button>
                            <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                            <button className="zoom-btn" onClick={handleZoomIn} title="Acercar">
                                <FaPlus />
                            </button>
                            <button className="zoom-btn reset-btn" onClick={handleZoomReset} title="Restablecer">
                                Reset
                            </button>
                        </div>
                    </div>
                    <div 
                        ref={previewRef}
                        className={`preview-content ${isDragging ? 'dragging' : ''}`}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    >
                        {invoice.file_url ? (
                            <iframe 
                                src={invoice.file_url} 
                                title={`Factura ${invoice.invoice_uuid}`}
                                style={{
                                    transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                                    width: `${100 / zoomLevel}%`,
                                    height: `${100 / zoomLevel}%`,
                                    minHeight: `${1800 / zoomLevel}px`
                                }}
                            />
                        ) : (
                            <div className="no-preview">
                                <FaFile className="no-preview-icon" />
                                <div className="no-preview-text">No hay vista previa disponible</div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="validation-steps-container">
                    <div className="steps-progress-bar">
                        {steps.map((s, index) => (
                            <React.Fragment key={s.id}>
                                <div className={`step-node ${
                                    step === s.id ? 'active' : 
                                    step > s.id ? 'completed' : ''
                                }`}>
                                    <div className="step-icon">{s.icon}</div>
                                    <div className="step-name">{s.name}</div>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`step-connector ${
                                        step > s.id ? 'completed' : 
                                        step === s.id + 1 ? 'active' : ''
                                    }`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className="step-content">
                        {loading || !prepared ? (
                            <div className="loading-state">
                                <p>Cargando datos de validación...</p>
                            </div>
                        ) : (
                            <>
                                {step === 1 && <ProviderValidator provider={prepared.provider} form={providerForm} setForm={setProviderForm} />}
                                {step === 2 && <ProductValidator localId={invoice.local_id} products={productsState} form={productForm} setForm={setProductForm} onReject={handleStubReject} onAccept={handleStubAccept} />}
                                {step === 3 && <BalanceValidation invoiceData={prepared.original_invoice as InvoiceData} globalValues={globalValues} setGlobalValues={setGlobalValues} itemValues={itemValues} setItemValues={setItemValues} includedItemIndices={acceptedItemIndices} />}
                                {step === 4 && execResult && <ResultSummary result={execResult} />}
                            </>
                        )}
                    </div>
                    
                    <div className="steps-footer">
                        {step < 4 && <Button variant="secondary" disabled={step === 1 || validating} onClick={() => setStep(s => s - 1)}><FaChevronLeft /> Atrás</Button>}
                        
                        {step < 3 && <Button variant="primary" onClick={() => setStep(s => s + 1)}>Siguiente <FaChevronRight /></Button>}
                        
                        {step === 3 && <Button variant="success" onClick={handleValidate} disabled={validating}>{validating ? 'Validando...' : 'Validar y Ejecutar'}</Button>}
                        
                        {step === 4 && (
                          <Button
                            variant="primary"
                            onClick={() => {
                              if (execResult) fetchPurchases();
                              onClose();
                            }}
                          >
                            Finalizar
                          </Button>
                        )}
                    </div>
                </div>
            </div>
        </Modal.Body>
    </Modal>
  );
};

export default Validator;
